import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../app/hooks';
import { getCompanyConfigDataAndSetFormData } from '../../store/company/configApiHelpers';
import { MANAGE_DROPDOWN, MANAGE_USERS_TABS } from '../../store/investor/constants';
import type { TermsAndConditionConfig } from '../../modules/Company/CompanyLogin/CompanyAdditionalDetailsForm/CompanyAdditionalDetailsForm';
import { blackManagePartners, purpleManagePartners } from '../../assets/hostedassets';
import ManageInvestorsIcon from '../../assets/Svg/ManageInvestorsIcon';
import LogoutIcon from '../../assets/Svg/Logouticon';
import { INVESTOR_ROUTES } from '../../utils/constants/routesConst';
import { VISIBILITY_KEYS } from '../../utils/constants/visibilityKeys';
import { useFeatureVisibility } from '../../utils/useVisibilityConfig';
import { getUserDataFromStorage, isPnoUser } from '../../utils/utils';
import type { ProfileOption } from '../ProfileDropdownSheet/ProfileDropdownSheet';

const PROFILE_VISIBILITY_KEYS = {
    manageInvestorsVisibility: VISIBILITY_KEYS.LENDER_MANAGEMENT.MANAGE_INVESTORS,
    managePartnersVisibility: VISIBILITY_KEYS.PARTNER_MANAGEMENT.MANAGE_PARTNERS,
};

/**
 * Builds the mobile header's profile dropdown options, mirroring the desktop
 * ProfileButton subset the mobile header shows: the two manage-user entries, logout,
 * and the Terms of use / Privacy policy footer.
 *
 * @param isOpen whether the dropdown is open — the terms/policy config is fetched on first open.
 * @param onLogout invoked by the Log out row; the header opens its confirmation modal.
 */
export const useMobileProfileOptions = ({ isOpen, onLogout }: { isOpen: boolean; onLogout: () => void }): ProfileOption[] => {
    const navigate = useNavigate();
    const userData = useAppSelector((state) => state.user.userData);
    const [termsAndPolicy, setTermsAndPolicy] = useState<TermsAndConditionConfig>();

    // An allow-list, so an absent, malformed or unrecognised accountType hides these entries
    // rather than falling through them. Off storage, never off the `userData` above — see
    // getUserDataFromStorage.
    const canManageUsers = isPnoUser(getUserDataFromStorage()?.accountType);

    const { manageInvestorsVisibility, managePartnersVisibility } = useFeatureVisibility(PROFILE_VISIBILITY_KEYS);

    useEffect(() => {
        if (!isOpen || termsAndPolicy) return;
        getCompanyConfigDataAndSetFormData(
            {
                accountType: userData?.accountType,
                primaryEmail: userData?.primaryEmail ?? userData?.investorPrimaryEmail,
                investorOrgId: userData?.investorOrgId,
            },
            { onSuccess: (config) => setTermsAndPolicy(config?.termsAndPolicyConfig || {}) },
        );
    }, [isOpen, termsAndPolicy, userData?.accountType, userData?.primaryEmail, userData?.investorPrimaryEmail, userData?.investorOrgId]);

    // `category` is the sole screen selector on ManageUser, and MANAGE_USER is a relative
    // route two levels above the mobile header — hence the absolute path.
    const goToManageUser = (category: string) => navigate(`${INVESTOR_ROUTES.APP}/${INVESTOR_ROUTES.MANAGE_USER}?category=${category}`);

    return [
        {
            label: MANAGE_DROPDOWN.MANAGE_INVESTORS,
            img: <ManageInvestorsIcon colour={'var(--primary-black-text-colour)'} height="20" width="20" />,
            hoverImg: <ManageInvestorsIcon colour={'var(--primary-text-colour)'} height="20" width="20" />,
            show: canManageUsers && manageInvestorsVisibility.show,
            action: () => goToManageUser(MANAGE_USERS_TABS.INVESTOR),
        },
        {
            label: MANAGE_DROPDOWN.MANAGE_PARTNERS,
            img: blackManagePartners,
            hoverImg: purpleManagePartners,
            show: canManageUsers && managePartnersVisibility.show,
            action: () => goToManageUser(MANAGE_USERS_TABS.PARTNER),
        },
        {
            // Cased to match the confirmation modal this row opens, one tap away.
            label: 'Log Out',
            img: <LogoutIcon color={'var(--primary-black-text-colour)'} height="15" width="15" />,
            hoverImg: <LogoutIcon color={'var(--primary-text-colour)'} height="15" width="15" />,
            show: true,
            action: onLogout,
        },
        {
            label: 'TermsAndPolicy',
            show: true,
            subOptions: [
                {
                    label: 'Terms of Use',
                    show: !!termsAndPolicy?.termsAndCondition,
                    action: () => window.open(termsAndPolicy?.termsAndCondition, '_blank', 'noopener,noreferrer'),
                },
                {
                    label: 'Privacy Policy',
                    show: !!termsAndPolicy?.privacyPolicy,
                    action: () => window.open(termsAndPolicy?.privacyPolicy, '_blank', 'noopener,noreferrer'),
                },
            ],
        },
    ];
};
